'use client';

import { AlignLeft, ArrowUpRight, ChevronLeft, ChevronRight, Paintbrush, Video } from 'lucide-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import logoWhite from '@/app/assets/imgs/logo-white.png';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { Footer } from '@/app/components/shared/Footer';
import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import { SITE } from '@/app/constants';
import { reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import {
  CARD2_VIDEO_POSTER,
  CARD2_VIDEO_SRC,
  CARD3_VIDEO_POSTER,
  CARD3_VIDEO_SRC,
  CARD4_VIDEO_POSTER,
  CARD4_VIDEO_SRC,
  HERO_VIDEO_MOBILE_SRC,
  HERO_VIDEO_POSTER,
  HERO_VIDEO_SRC,
  MODEL_IMAGES,
  NEWS_IMAGES,
} from '../_constants/media';
import { BODY, OVERLAY_BODY, OVERLAY_TITLE, SECTION_TITLE, SUBHEAD } from '../_constants/typography';
import { QuestionSection } from './QuestionSection';

const FEATURE_KEYS = ['f1', 'f2', 'f3', 'f4'] as const;
const MODEL_KEYS = ['r1', 's1'] as const;
const NEWS_KEYS = ['n1', 'n2', 'n3', 'n4'] as const;

// Every news row points at the blog index for now; swap in per-item URLs once
// the entries are backed by real posts.
const NEWS_INDEX_LINK = '/blog';

// Each model row links to its own product page. R1's product page is this one,
// so its CTA goes to the create flow instead of reloading in place.
const MODEL_LINKS: Record<(typeof MODEL_KEYS)[number], string> = {
  r1: '/create/text2video',
  s1: '/vidu-stream',
};

/** Hero carousel advances on its own every 8s. */
const AUTOPLAY_MS = 8000;

/** Stagger delay bucket for `data-reveal-delay`; the stylesheet defines 0–5. */
const revealDelay = (i: number) => String(Math.min(i, 5));

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>('[data-reveal]');

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        }
      },
      {
        // Pull the trigger line up off the bottom edge. Without this a block
        // fires the moment a sliver crosses into view and the animation is
        // already over by the time it is actually on screen.
        rootMargin: '0px 0px -32% 0px',
        threshold: 0.01,
      },
    );

    // Anything already on screen at mount (the hero) reveals right away rather
    // than waiting for the trigger line, which sits below it.
    for (const el of Array.from(els)) {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        el.classList.add('is-visible');
        continue;
      }
      io.observe(el);
    }

    return () => io.disconnect();
  }, []);
}

export const ViduR1: React.FC = () => {
  const t = useTranslations('vidu-r1');
  const router = useRouter();
  useReveal();

  useEffect(() => {
    reportEvent('page_view', { page_name: 'vidu_r1_page' });
  }, []);

  const handleTryNow = () => {
    reportEvent('element_click', { element_name: 'vidu-r1-try-now', page: 'vidu-r1' });
    router.push('/create/text2video');
  };

  // ── Carousel state ──
  const carouselRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  // Hero carousel cards: main R1 card + one card per feature
  const heroCards = [
    { key: 'hero', videoSrc: HERO_VIDEO_SRC, mobileVideoSrc: HERO_VIDEO_MOBILE_SRC, poster: HERO_VIDEO_POSTER },
    { key: 'f1', videoSrc: CARD2_VIDEO_SRC, poster: CARD2_VIDEO_POSTER },
    { key: 'f2', videoSrc: CARD3_VIDEO_SRC, poster: CARD3_VIDEO_POSTER },
    { key: 'f3', videoSrc: CARD4_VIDEO_SRC, poster: CARD4_VIDEO_POSTER },
  ];

  // Infinite loop: render the deck three times and keep the viewport parked in
  // the middle copy. Scrolling past either edge silently teleports back by one
  // deck width, so the tail card is always visible to the left of the head.
  const DECK = heroCards.length;
  const loopCards = [...heroCards, ...heroCards, ...heroCards];

  // Scroll offset that puts a card's center at the viewport center.
  const centerOffsetFor = (container: HTMLElement, card: HTMLElement) =>
    card.offsetLeft + card.offsetWidth / 2 - container.clientWidth / 2;

  // Center the middle-copy instance of a logical index, without animation.
  const jumpToLogical = (container: HTMLElement, logical: number) => {
    const card = container.children[DECK + logical] as HTMLElement | undefined;
    if (!card) return;
    container.scrollLeft = centerOffsetFor(container, card);
  };

  // Step by ±1 (or jump to a tapped card) and let the scroll handler normalize.
  const scrollByCards = useCallback((delta: number) => {
    const container = carouselRef.current;
    if (!container) return;
    const first = container.children[0] as HTMLElement | undefined;
    if (!first) return;
    const stride = first.offsetWidth + 16; // card width + gap-4
    container.scrollBy({ left: delta * stride, behavior: 'smooth' });
  }, []);

  const scrollToCard = useCallback(
    (logical: number) => {
      const container = carouselRef.current;
      if (!container) return;
      scrollByCards(((logical - activeIdx + DECK * 1.5) % DECK) - DECK / 2);
    },
    [activeIdx, DECK, scrollByCards],
  );

  // Park in the middle copy on mount.
  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;
    jumpToLogical(container, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Autoplay ──
  // Paused while the pointer rests on the stage or a touch drag is in flight.
  // `autoplayTick` bumps on manual navigation so the countdown restarts from 0
  // instead of firing right after a user-driven step.
  const [isPaused, setIsPaused] = useState(false);
  const [autoplayTick, setAutoplayTick] = useState(0);
  const restartAutoplay = useCallback(() => setAutoplayTick((n) => n + 1), []);

  const handlePrev = () => {
    scrollByCards(-1);
    restartAutoplay();
  };
  const handleNext = () => {
    scrollByCards(1);
    restartAutoplay();
  };

  useEffect(() => {
    if (isPaused) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const id = setInterval(() => {
      // Skip ticks on a backgrounded tab: the queued smooth-scrolls would all
      // land at once when the user comes back.
      if (document.hidden) return;
      scrollByCards(1);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [isPaused, autoplayTick, scrollByCards]);

  // Track the centered card and wrap the scroll position back into the middle copy.
  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;

    const centeredIndex = () => {
      const viewportCenter = container.scrollLeft + container.clientWidth / 2;
      let closest = 0;
      let minDist = Number.POSITIVE_INFINITY;
      Array.from(container.children).forEach((child, i) => {
        const el = child as HTMLElement;
        const dist = Math.abs(el.offsetLeft + el.offsetWidth / 2 - viewportCenter);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      return closest;
    };

    let rafId = 0;
    let settleId: ReturnType<typeof setTimeout>;

    const onScroll = () => {
      // Highlight follows the centered card immediately.
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => setActiveIdx(centeredIndex() % DECK));

      // Teleport only once scrolling has stopped, so we never cut off a
      // smooth-scroll animation mid-flight.
      clearTimeout(settleId);
      settleId = setTimeout(() => {
        const closest = centeredIndex();
        const first = container.children[0] as HTMLElement | undefined;
        if (!first) return;
        const deckWidth = (first.offsetWidth + 16) * DECK;
        if (closest < DECK) {
          container.scrollLeft += deckWidth;
        } else if (closest >= DECK * 2) {
          container.scrollLeft -= deckWidth;
        }
      }, 140);
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafId);
      clearTimeout(settleId);
    };
  }, [DECK]);

  return (
    <>
      <style>{`
        [data-reveal] {
          opacity: 0;
          transform: translateY(64px);
          transition: opacity 0.9s cubic-bezier(0.25,0,0,1),
                      transform 0.9s cubic-bezier(0.25,0,0,1);
          will-change: opacity, transform;
        }
        /* News cards travel further still, so the motion reads as each card
           rising into place as it scrolls in. */
        [data-reveal="rise"] {
          transform: translateY(96px);
          transition-duration: 1s;
        }
        [data-reveal].is-visible {
          opacity: 1;
          transform: none;
          will-change: auto;
        }
        [data-reveal][data-reveal-delay="1"] { transition-delay: 90ms; }
        [data-reveal][data-reveal-delay="2"] { transition-delay: 180ms; }
        [data-reveal][data-reveal-delay="3"] { transition-delay: 270ms; }
        [data-reveal][data-reveal-delay="4"] { transition-delay: 360ms; }
        [data-reveal][data-reveal-delay="5"] { transition-delay: 450ms; }
        @media (prefers-reduced-motion: reduce) {
          [data-reveal] {
            opacity: 1 !important;
            transform: none !important;
            transition: none !important;
          }
        }
      `}</style>

      <div className="min-h-screen bg-[#0a0a0a] text-white">
        {/* ── 0. Header ── fixed to the viewport, stays visible while scrolling */}
        <header className="fixed inset-x-0 top-0 z-30 bg-[#0a0a0a]/70 backdrop-blur-xl">
          <div className="mx-auto flex h-[88px] w-full max-w-[1920px] items-center justify-between px-[9%] sm:h-[68px] sm:px-6">
            <ClientLink href="/" aria-label="Vidu" className="relative h-11 w-[104px] sm:h-9 sm:w-[82px]">
              <Image src={logoWhite} alt="Vidu" fill sizes="104px" className="object-contain object-left" priority />
            </ClientLink>
            <div className="flex items-center gap-3 sm:gap-2">
              <a
                href={SITE === 'china' ? 'https://platform.vidu.cn/docs' : 'https://platform.vidu.com/docs'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-11 items-center rounded-full border border-white/20 px-6 font-medium text-[14px] text-white/80 transition-colors hover:border-white/40 hover:text-white sm:h-9 sm:px-4 sm:text-[13px]"
              >
                API
              </a>
              <button
                type="button"
                onClick={handleTryNow}
                className="inline-flex h-11 items-center rounded-full bg-white px-6 font-medium text-[14px] text-black transition-colors hover:bg-white/90 sm:h-9 sm:px-4 sm:text-[13px]"
              >
                {t('buttons.tryNow')}
              </button>
            </div>
          </div>
        </header>

        {/* Safe area: every block lives inside this centered 1920px column.
            Top padding clears the fixed header. */}
        <div className="mx-auto w-full max-w-[1920px] pt-[88px] sm:pt-[68px]">
          {/* ── 1. Hero Carousel ── */}
          <section className="bg-[#0a0a0a] pt-1 sm:pt-0">
            {/* Stage. The track's side padding is what makes the neighbouring
              cards peek in; the arrows sit on top of those peeking edges. */}
            <div
              className="relative w-full"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              onTouchStart={() => setIsPaused(true)}
              onTouchEnd={() => setIsPaused(false)}
              onTouchCancel={() => setIsPaused(false)}
            >
              {/* Arrows overlaid on the peeking side cards */}
              <button
                type="button"
                aria-label="Previous"
                onClick={handlePrev}
                className="-translate-y-1/2 absolute top-1/2 left-4 z-10 flex size-11 items-center justify-center rounded-full bg-black/40 text-white/80 backdrop-blur-md transition-colors hover:bg-black/60 hover:text-white sm:left-2 sm:size-9"
              >
                <ChevronLeft className="size-5 sm:size-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                aria-label="Next"
                onClick={handleNext}
                className="-translate-y-1/2 absolute top-1/2 right-4 z-10 flex size-11 items-center justify-center rounded-full bg-black/40 text-white/80 backdrop-blur-md transition-colors hover:bg-black/60 hover:text-white sm:right-2 sm:size-9"
              >
                <ChevronRight className="size-5 sm:size-4" strokeWidth={2} />
              </button>

              {/* Card track */}
              <div
                ref={carouselRef}
                className="flex snap-x snap-mandatory items-center gap-4 overflow-x-auto px-[6%] pb-0 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] sm:gap-3 sm:px-[5%] [&::-webkit-scrollbar]:hidden"
              >
                {loopCards.map((card, absIdx) => {
                  // Logical index within one deck; the same card appears 3×.
                  const i = absIdx % DECK;
                  return (
                    <div
                      key={`${card.key}-${absIdx}`}
                      className={`relative aspect-video h-[calc(100svh-112px)] max-h-[900px] w-auto max-w-full shrink-0 cursor-pointer snap-center overflow-hidden rounded-[32px] transition-opacity duration-500 ease-out sm:h-auto sm:w-full sm:rounded-[24px] ${
                        i === activeIdx ? 'opacity-100' : 'opacity-40'
                      }`}
                      onClick={() => {
                        scrollToCard(i);
                        restartAutoplay();
                      }}
                    >
                      {/* background */}
                      {card.videoSrc ? (
                        <VideoLazyLoad
                          className="absolute inset-0 size-full"
                          videoSrc={card.videoSrc}
                          mobileVideoSrc={card.mobileVideoSrc ?? undefined}
                          poster={card.poster}
                          priority={absIdx === DECK}
                        />
                      ) : (
                        <img
                          src={card.poster}
                          alt=""
                          aria-hidden="true"
                          className="absolute inset-0 size-full object-cover"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                      {/* content stacked at the bottom: title → subtitle → pills */}
                      {i === 0 ? (
                        <div className="absolute inset-x-0 bottom-0 flex flex-col items-center px-8 pb-10 sm:px-5 sm:pb-6">
                          <h1 className="text-center font-semibold text-[88px] leading-[0.95] tracking-[-3px] sm:text-[40px] sm:tracking-[-1.5px]">
                            {t('title')}
                          </h1>
                          <p className={`mt-4 max-w-[520px] text-center font-normal text-white/75 sm:mt-2 ${BODY}`}>
                            {t('subtitle')}
                          </p>
                          <div className="mt-7 flex flex-wrap justify-center gap-3 sm:mt-4 sm:gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTryNow();
                              }}
                              className="h-11 rounded-full bg-white px-7 font-medium text-[14px] text-black transition-colors hover:bg-white/90 sm:h-9 sm:px-5 sm:text-[13px]"
                            >
                              {t('buttons.tryNow')}
                            </button>
                            {SITE === 'global' && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  reportEvent('element_click', { element_name: 'vidu-r1-learn-more', page: 'vidu-r1' });
                                  document.getElementById('vidu-r1-intro')?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                className="h-11 rounded-full border border-white/30 bg-white/10 px-7 font-medium text-[14px] text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:h-9 sm:px-5 sm:text-[13px]"
                              >
                                {t('buttons.learnMore')}
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="absolute inset-x-0 bottom-0 flex flex-col items-center px-10 pb-10 text-center sm:px-6 sm:pb-6">
                          <h2 className={`font-semibold tracking-[-1.5px] sm:tracking-[-0.5px] ${SECTION_TITLE}`}>
                            {t(`features.f${i}.title`)}
                          </h2>
                          <p className={`mt-4 max-w-[420px] font-normal text-white/70 sm:mt-2 ${BODY}`}>
                            {t(`features.f${i}.description`)}
                          </p>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTryNow();
                            }}
                            className="mt-7 h-11 rounded-full bg-white px-7 font-medium text-[14px] text-black transition-colors hover:bg-white/90 sm:mt-4 sm:h-9 sm:px-5 sm:text-[13px]"
                          >
                            {t('buttons.tryNow')}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ── 2. What's new ── icon cards in a grid */}
          <section id="vidu-r1-intro" className="bg-[#0a0a0a] px-[9%] py-24 sm:px-6 sm:py-16">
            <h2
              data-reveal
              className={`mb-14 text-balance text-center font-semibold tracking-[-1.5px] sm:mb-8 sm:tracking-[-0.5px] ${SECTION_TITLE}`}
            >
              {t('features.title')}
            </h2>
            <div className="grid grid-cols-3 gap-7 sm:grid-cols-1 sm:gap-8">
              {FEATURE_KEYS.slice(0, 3).map((key, i) => (
                <div key={key} data-reveal data-reveal-delay={revealDelay(i)}>
                  {/* large rounded tile with a centered icon */}
                  <div className="flex aspect-[16/10] items-center justify-center rounded-[20px] bg-white/[0.045]">
                    {
                      [
                        <Video key="video" className="size-[68px] text-white sm:size-14" strokeWidth={1.25} />,
                        <AlignLeft key="align" className="size-[68px] text-white sm:size-14" strokeWidth={1.25} />,
                        <Paintbrush key="brush" className="size-[68px] text-white sm:size-14" strokeWidth={1.25} />,
                      ][i]
                    }
                  </div>
                  <h3 className={`mt-6 font-medium sm:mt-4 ${SUBHEAD}`}>{t(`features.${key}.title`)}</h3>
                  <p className={`mt-2 font-normal text-white/45 ${BODY}`}>{t(`features.${key}.description`)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── 3. Model family ── one row per model, stacked */}
          <section className="bg-[#0a0a0a] px-[9%] py-20 sm:px-6 sm:py-14">
            <h2
              data-reveal
              className={`mb-14 text-balance text-center font-semibold tracking-[-1.5px] sm:mb-8 sm:tracking-[-0.5px] ${SECTION_TITLE}`}
            >
              {t('models.sectionTitle')}
            </h2>
            {MODEL_KEYS.map((modelKey, row) => (
              <div
                key={modelKey}
                data-reveal
                /* Divider between rows only, so the section keeps clean outer edges. */
                className={`grid grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] items-center gap-x-8 py-[94px] sm:grid-cols-1 sm:gap-x-0 sm:py-[68px] ${
                  row > 0 ? 'border-white/[0.09] border-t' : ''
                }`}
              >
                {/* left — name, latin mark, description and CTA, stacked */}
                <div>
                  <p className="font-semibold text-[34px] leading-[1.15] tracking-[-1px] sm:text-[26px]">
                    {t(`models.${modelKey}.name`)}
                  </p>
                  <p className={`mt-1 text-white/40 ${SUBHEAD}`}>{t(`models.${modelKey}.latin`)}</p>
                  <p className={`mt-6 text-white/70 sm:mt-4 ${BODY}`}>{t(`models.${modelKey}.description`)}</p>
                  <ClientLink
                    href={MODEL_LINKS[modelKey]}
                    onClick={() =>
                      reportEvent('element_click', {
                        element_name: `vidu-r1-model-${modelKey}-learn-more`,
                        page: 'vidu-r1',
                      })
                    }
                    className={`mt-7 inline-flex h-10 items-center gap-1.5 rounded-full bg-white/[0.12] px-5 font-medium text-white transition-colors hover:bg-white/20 sm:mt-5 ${OVERLAY_TITLE}`}
                  >
                    {t(`models.${modelKey}.cta`)}
                    <ArrowUpRight className="size-4" strokeWidth={2} />
                  </ClientLink>
                </div>

                {/* right — model key visual */}
                <div className="overflow-hidden rounded-[20px] bg-white/[0.045] sm:mt-8 sm:rounded-[16px]">
                  <img
                    src={MODEL_IMAGES[modelKey]}
                    alt=""
                    aria-hidden="true"
                    className="aspect-[16/10] w-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </section>

          {/* ── 4. Latest ── model updates and news */}
          <section className="bg-[#0a0a0a] px-[9%] py-20 sm:px-6 sm:py-14">
            <h2
              data-reveal
              className={`mb-12 text-balance font-semibold tracking-[-1.5px] sm:mb-8 sm:tracking-[-0.5px] ${SECTION_TITLE}`}
            >
              {t('latest.sectionTitle')}
            </h2>

            <ul className="grid grid-cols-2 gap-x-14 gap-y-14 sm:grid-cols-1 sm:gap-y-10">
              {NEWS_KEYS.map((key, i) => (
                // Stagger by column, not by absolute index: each row scrolls
                // into view on its own, so the delay should restart per row.
                <li key={key} data-reveal="rise" data-reveal-delay={revealDelay(i % 2)}>
                  <ClientLink
                    href={NEWS_INDEX_LINK}
                    onClick={() =>
                      reportEvent('element_click', { element_name: `vidu-r1-news-${key}`, page: 'vidu-r1' })
                    }
                    className="group flex h-full flex-col"
                  >
                    <div className="overflow-hidden rounded-[20px] bg-white/[0.045] sm:rounded-[16px]">
                      <img
                        src={NEWS_IMAGES[i]}
                        alt=""
                        aria-hidden="true"
                        className="aspect-[16/10] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                    </div>
                    <h3
                      className={`mt-6 text-balance font-medium transition-colors group-hover:text-white/80 sm:mt-4 ${SUBHEAD}`}
                    >
                      {t(`latest.${key}.title`)}
                    </h3>
                    <p className={`mt-2 text-white/45 ${BODY}`}>{t(`latest.${key}.desc`)}</p>
                    {/* meta row: date · tag · learn more. `mt-auto` keeps it on
                        the card's bottom edge so rows stay aligned even when
                        titles wrap to a different number of lines. */}
                    <div
                      className={`mt-auto flex items-center gap-6 pt-5 text-white/40 sm:gap-4 sm:pt-4 ${OVERLAY_BODY}`}
                    >
                      <time dateTime={t(`latest.${key}.date`).replaceAll('.', '-')} className="tabular-nums">
                        {t(`latest.${key}.date`)}
                      </time>
                      <span>{t(`latest.${key}.tag`)}</span>
                      <span className="inline-flex items-center gap-1 transition-colors group-hover:text-white">
                        {t('latest.more')}
                        <ChevronRight
                          className="size-3.5 transition-transform group-hover:translate-x-0.5"
                          strokeWidth={2}
                        />
                      </span>
                    </div>
                  </ClientLink>
                </li>
              ))}
            </ul>
          </section>

          {/* ── 5. FAQ ── */}
          <section className="border-white/[0.07] border-t">
            <QuestionSection />
          </section>

          {/* ── 6. Footer ── */}
          <Footer className="w-full" theme="dark" />
        </div>
      </div>
    </>
  );
};
